// The layout of the supply flow: four columns of nodes, each node a slot as
// tall as its product count, and between adjacent columns the bands as
// filled ribbons that share the slots of their two ends. The module is pure
// and deterministic: the response and a size in, positions and path strings
// out. It measures nothing, so that it runs without a DOM.

import {
  SupplyFlow,
  SupplyFlowBand,
  SupplyFlowColumnId,
  SupplyFlowNode,
} from "../../../types/analytics";

export const COLUMN_IDS: readonly SupplyFlowColumnId[] = [
  "hosts",
  "kinds",
  "makers",
  "countries",
];

// How each column is headed
export const COLUMN_TITLES: Record<SupplyFlowColumnId, string> = {
  hosts: "Hosts · kinds of host",
  kinds: "Kinds",
  makers: "Makers",
  countries: "Countries",
};

export const LAYOUT = {
  /** Size of the drawing when the container has not been measured */
  fallbackWidth: 1200,
  fallbackHeight: 680,
  /** Narrowest and shortest the drawing is laid out at */
  minWidth: 900,
  minHeight: 360,
  /** Room left at each side of the drawing */
  sideMargin: 40,
  /** Room above the nodes, for the column heads */
  headerHeight: 48,
  /** Room under the lowest node */
  bottomMargin: 24,
  nodeWidth: 170,
  /** Vertical gap between two nodes of a column */
  nodeGap: 8,
  /** The gap between two nodes that are both too short for a name */
  sliverGap: 2,
  /** Least height of a firm node: its name on one line */
  minNodeHeight: 18,
  /** Least height of a node under the support floor: a sliver */
  minThinHeight: 3,
  /** From this height a node holds its name over its count */
  fullLabelHeight: 40,
  /** From this height a node holds its name and count on one line */
  lineLabelHeight: 16,
  /** Height of one product where the columns do not fit the canvas */
  minUnit: 0.5,
  /** Height of one product at most, so that a small flow is not blown up */
  maxUnit: 24,
} as const;

// What a node has room to say: its name over its count, both on one line,
// or nothing (the chart then names it on hover)
export type NodeLabel = "full" | "line" | "none";

export interface PlacedNode {
  node: SupplyFlowNode;
  column: SupplyFlowColumnId;
  x: number;
  y: number;
  width: number;
  height: number;
  label: NodeLabel;
}

export interface PlacedBand {
  /** Position in the response's bands: the identity of the band */
  index: number;
  band: SupplyFlowBand;
  /** Where the ribbon leaves the source node, and where it meets the target */
  x0: number;
  x1: number;
  sourceTop: number;
  sourceBottom: number;
  targetTop: number;
  targetBottom: number;
  /** The closed path of the ribbon */
  path: string;
}

export interface PlacedColumn {
  id: SupplyFlowColumnId;
  title: string;
  x: number;
}

export interface SupplyFlowLayout {
  width: number;
  height: number;
  columns: PlacedColumn[];
  nodes: PlacedNode[];
  bands: PlacedBand[];
}

const round = (value: number): number => Math.round(value * 100) / 100;

// A count as the layout reads it: never negative, never not a number
const countOf = (value: number): number =>
  Number.isFinite(value) && value > 0 ? value : 0;

// The ribbon between two ranges, as a closed path: the top edge as a cubic
// curve from the source to the target, the bottom edge back
export const ribbonPath = (
  x0: number,
  sourceTop: number,
  sourceBottom: number,
  x1: number,
  targetTop: number,
  targetBottom: number,
): string => {
  const mid = round((x0 + x1) / 2);
  return [
    `M${round(x0)},${round(sourceTop)}`,
    `C${mid},${round(sourceTop)} ${mid},${round(targetTop)} ${round(x1)},${round(targetTop)}`,
    `L${round(x1)},${round(targetBottom)}`,
    `C${mid},${round(targetBottom)} ${mid},${round(sourceBottom)} ${round(x0)},${round(sourceBottom)}`,
    "Z",
  ].join(" ");
};

const heightOf = (node: SupplyFlowNode, unit: number): number =>
  Math.max(
    countOf(node.product_count) * unit,
    node.thin ? LAYOUT.minThinHeight : LAYOUT.minNodeHeight,
  );

// The gap under a node: two slivers sit close, anything named keeps apart
const gapAfter = (height: number, next: number): number =>
  height < LAYOUT.lineLabelHeight && next < LAYOUT.lineLabelHeight
    ? LAYOUT.sliverGap
    : LAYOUT.nodeGap;

// The heights of a column's nodes at a unit, and the room they take
const stack = (
  nodes: SupplyFlowNode[],
  unit: number,
): { heights: number[]; total: number } => {
  const heights = nodes.map((node) => heightOf(node, unit));
  let total = 0;
  heights.forEach((height, index) => {
    total += height;
    if (index < heights.length - 1) {
      total += gapAfter(height, heights[index + 1]);
    }
  });
  return { heights, total };
};

// The height of one product: the largest at which the tallest column fits
// the plot. Where even the least does not fit, the least is kept and the
// drawing grows past the canvas, which then scrolls.
const unitHeight = (flow: SupplyFlow, plot: number): number => {
  const tallest = (unit: number): number =>
    Math.max(0, ...flow.columns.map((column) => stack(column.nodes, unit).total));
  if (tallest(LAYOUT.maxUnit) <= plot) {
    return LAYOUT.maxUnit;
  }
  if (tallest(LAYOUT.minUnit) > plot) {
    return LAYOUT.minUnit;
  }
  let low: number = LAYOUT.minUnit;
  let high: number = LAYOUT.maxUnit;
  for (let step = 0; step < 32; step += 1) {
    const mid = (low + high) / 2;
    if (tallest(mid) <= plot) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return low;
};

const labelOf = (height: number): NodeLabel =>
  height >= LAYOUT.fullLabelHeight
    ? "full"
    : height >= LAYOUT.lineLabelHeight
      ? "line"
      : "none";

const sizeOf = (requested: number | undefined, fallback: number, least: number) =>
  Math.max(
    requested !== undefined && Number.isFinite(requested) ? requested : fallback,
    least,
  );

export const layoutSupplyFlow = (
  flow: SupplyFlow,
  requestedWidth?: number,
  requestedHeight?: number,
): SupplyFlowLayout => {
  const width = sizeOf(requestedWidth, LAYOUT.fallbackWidth, LAYOUT.minWidth);
  const canvas = sizeOf(requestedHeight, LAYOUT.fallbackHeight, LAYOUT.minHeight);
  const plot = canvas - LAYOUT.headerHeight - LAYOUT.bottomMargin;
  const unit = unitHeight(flow, plot);
  const span = width - 2 * LAYOUT.sideMargin - LAYOUT.nodeWidth;

  const columns: PlacedColumn[] = [];
  const nodes: PlacedNode[] = [];
  const byId = new Map<string, PlacedNode>();
  const order = new Map<string, number>();
  let bottom: number = LAYOUT.headerHeight;

  COLUMN_IDS.forEach((id, position) => {
    const x = round(LAYOUT.sideMargin + (span * position) / (COLUMN_IDS.length - 1));
    columns.push({ id, title: COLUMN_TITLES[id], x });
    const column = flow.columns.find((candidate) => candidate.id === id);
    const members = column?.nodes ?? [];
    const { heights } = stack(members, unit);
    let y: number = LAYOUT.headerHeight;
    // The response's order is kept: count descending, buckets last
    members.forEach((node, index) => {
      const height = round(heights[index]);
      const placed: PlacedNode = {
        node,
        column: id,
        x,
        y: round(y),
        width: LAYOUT.nodeWidth,
        height,
        label: labelOf(height),
      };
      nodes.push(placed);
      byId.set(node.id, placed);
      order.set(node.id, index);
      bottom = Math.max(bottom, placed.y + height);
      if (index < members.length - 1) {
        y += height + gapAfter(heights[index], heights[index + 1]);
      }
    });
  });

  // The bands that join two nodes of adjacent columns, in the order each
  // node's side hands out its range: by the partner's slot, a need last
  const columnOf = (id: string): number => {
    const placed = byId.get(id);
    return placed ? COLUMN_IDS.indexOf(placed.column) : -1;
  };
  const joined = flow.bands
    .map((band, index) => ({ band, index }))
    .filter(
      ({ band }) =>
        columnOf(band.source) >= 0 &&
        columnOf(band.target) === columnOf(band.source) + 1,
    );
  const rank = (a: { band: SupplyFlowBand; index: number }, b: typeof a, end: "source" | "target") => {
    const partner = end === "source" ? "target" : "source";
    return (
      (order.get(a.band[partner]) ?? 0) - (order.get(b.band[partner]) ?? 0) ||
      Number(a.band.need) - Number(b.band.need) ||
      a.index - b.index
    );
  };

  // Each side of a node gives its bands consecutive ranges of its slot, in
  // proportion to their counts: the slot stands for the node's count, and
  // where the bands sum above it (a kind's maker bands may) for their sum.
  // A band is so never taller than the slot, however short the slot is.
  const ranges = new Map<string, { top: number; bottom: number }>();
  const allocate = (end: "source" | "target") => {
    const groups = new Map<string, typeof joined>();
    for (const entry of joined) {
      const key = entry.band[end];
      groups.set(key, [...(groups.get(key) ?? []), entry]);
    }
    groups.forEach((group, id) => {
      const slot = byId.get(id)!;
      const sorted = [...group].sort((a, b) => rank(a, b, end));
      const total = sorted.reduce((sum, { band }) => sum + countOf(band.product_count), 0);
      const whole = Math.max(total, countOf(slot.node.product_count));
      const scale = whole > 0 ? slot.height / whole : 0;
      const floor = slot.y + slot.height;
      let cursor = slot.y;
      for (const { band, index } of sorted) {
        const top = Math.min(cursor, floor);
        const size = countOf(band.product_count) * scale;
        cursor = Math.min(cursor + size, floor);
        ranges.set(`${end}:${index}`, { top: round(top), bottom: round(cursor) });
      }
    });
  };
  allocate("source");
  allocate("target");

  const bands: PlacedBand[] = joined.map(({ band, index }) => {
    const source = byId.get(band.source)!;
    const target = byId.get(band.target)!;
    const from = ranges.get(`source:${index}`)!;
    const to = ranges.get(`target:${index}`)!;
    const x0 = round(source.x + source.width);
    const x1 = target.x;
    return {
      index,
      band,
      x0,
      x1,
      sourceTop: from.top,
      sourceBottom: from.bottom,
      targetTop: to.top,
      targetBottom: to.bottom,
      path: ribbonPath(x0, from.top, from.bottom, x1, to.top, to.bottom),
    };
  });

  // Never shorter than the canvas, so that the surface is filled
  const height = Math.max(canvas, round(bottom + LAYOUT.bottomMargin));
  return { width, height, columns, nodes, bands };
};
